import {
  FeaturedCard,
  Game,
  GameSearchStatus,
} from "@onmo/graphql/graphql";
import { isYouTubeUrl } from "./media";
import { map, uniqBy, upperFirst } from "lodash-es";
import { GameMetadata } from "../types/gamePack";

type GameImages = Record<string, string | undefined>;

const EMPTY_METADATA = {} as GameMetadata;
const metadataCache = new WeakMap<object, GameMetadata>();

export const getGameMetadata = (
  game?: Pick<Game, "metadata"> | null,
): GameMetadata => {
  if (!game?.metadata) return EMPTY_METADATA;
  const cached = metadataCache.get(game);
  if (cached) return cached;
  try {
    const parsed = JSON.parse(game.metadata) as GameMetadata;
    metadataCache.set(game, parsed);
    return parsed;
  } catch {
    metadataCache.set(game, EMPTY_METADATA);
    return EMPTY_METADATA;
  }
};

export const getImageWithFallback = (
  gameImages?: GameImages,
  baseKey?: string,
  newKey?: string,
) => {
  if (!gameImages) return undefined;
  const collect = (key?: string) =>
    key
      ? [gameImages[key], gameImages[`${key}2x`], gameImages[`${key}3x`]]
      : [];

  return [...collect(newKey), ...collect(baseKey)].find(Boolean);
};

export const sortYouTubeFirst = <T extends string | Record<string, unknown>>(
  items: T[],
  getUrl?: (item: T) => string,
): T[] => {
  const resolve = getUrl || ((item: T) => item as string);

  return items
    .reduce<[T[], T[]]>(
      ([yt, other], item) => {
        isYouTubeUrl(resolve(item)) ? yt.push(item) : other.push(item);
        return [yt, other];
      },
      [[], []],
    )
    .flat();
};

export const checkBYOGGame = (game?: Game) =>
  game?.category?.includes("BYOG") ?? false;

export const isEssentialGame = (game?: Game) =>
  game?.category?.includes("PID") ?? false;

export const getSearchGameTitle = (
  query: string,
  status?: GameSearchStatus,
) => {
  switch (status) {
    case GameSearchStatus.MatchFound:
      return "Top Results";
    case GameSearchStatus.MatchFoundNotPlayable:
      return `'${query}' is unavailable. Try these suggestions instead.`;
    case GameSearchStatus.LabelMatchFound:
      return `${upperFirst(query)} Games`;
    case GameSearchStatus.NotFound:
      return "More to explore";
    default:
      return "";
  }
};

export const getGames = (items?: FeaturedCard[]) =>
  uniqBy(map(items, "data.game"), "id").filter((game) => Boolean(game));

export const getLicenseTag = (price: number, game?: Game): string => {
  if (!game) return "";
  if (isEssentialGame(game)) return "Essential Pass";
  if (!checkBYOGGame(game)) return "";
  return price === 0 ? "Free-to-Play" : "Ownership Required";
};

export const getGameRating = (rating: number) => {
  if (!rating) return "N/A";
  const ratingFormat = rating / 10;
  return (Math.round(ratingFormat * 10) / 10).toFixed(1);
};
